import { CallDto } from '@act/shared/data-transfer-objects';
import { CALL_CONNECTION_STATUSES_TO_IGNORE } from './activeCallMenu.constants';

/**
 * Deduplicates connections by userId + phoneNumber, preferring active over completed.
 *
 * @param connections Raw call connections.
 * @returns Deduplicated connections.
 */
export function dedupeCallConnections(
  connections: CallDto['connections']
): CallDto['connections'] {
  const connectionMap = new Map<string, CallDto['connections'][0]>();

  for (const connection of connections) {
    const key = `${connection.userId || 'null'}-${connection.phoneNumber}`;
    const existing = connectionMap.get(key);

    if (!existing) {
      connectionMap.set(key, connection);
    } else {
      const isCurrentActive = !connection.completedAt;
      const isExistingActive = !existing.completedAt;

      if (isCurrentActive && !isExistingActive) {
        connectionMap.set(key, connection);
      }
    }
  }

  return Array.from(connectionMap.values());
}

/**
 * MUI chip / icon color token for a Twilio connection status string.
 *
 * @param status Raw or display status label (case-insensitive).
 * @returns Palette severity key.
 */
export function getStatusColor(
  status: string
): 'success' | 'warning' | 'default' | 'error' | 'info' {
  switch (status.toLowerCase()) {
    case 'connected':
    case 'in-progress':
      return 'success';
    case 'calling':
    case 'initiated':
    case 'on hold':
      return 'warning';
    case 'completed':
      return 'default';
    case 'failed':
    case 'busy':
    case 'no-answer':
    case 'canceled':
    case 'unable to connect':
      return 'error';
    default:
      return 'info';
  }
}

/**
 * Whether a connection counts as active for participant limits.
 *
 * @param connection Call connection.
 * @returns True when still in progress.
 */
export function isActiveCallConnection(
  connection: CallDto['connections'][0]
): boolean {
  return (
    !connection.completedAt &&
    !(CALL_CONNECTION_STATUSES_TO_IGNORE as readonly string[]).includes(
      connection.status
    )
  );
}
